import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Calendar,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  TIMELINE_OPTIONS,
  type CommonQuestionnaireData,
  type QuestionnaireSubmission,
} from "@/domain/intake";

const STORAGE_KEY = "echopeak-questionnaire-progress";

interface LeadContext {
  leadId: string;
  mode?: "call" | "package";
  email: string;
  name: string;
  packageId: string | null;
  timezone: string;
  ndaRequired: boolean;
  emailVerified?: boolean;
  ndaSigned?: boolean;
  engagementPath?: string;
  summary: string;
}

function getLeadContext(): LeadContext | null {
  try {
    const raw = localStorage.getItem("echopeak-lead-context");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const initialCommon: CommonQuestionnaireData = {
  problemToSolve: "",
  successCriteria: "",
  timeline: "",
  technicalEnvironment: "",
  constraints: "",
  decisionMakers: "",
};

interface QuestionMeta {
  id: string;
  section: "common";
  field: string;
  title: string;
  subtitle?: string;
  required?: boolean;
  type: "textarea" | "input" | "select" | "multiselect" | "tier-select" | "yes-no";
  placeholder?: string;
  options?: { id: string; label: string }[];
  rows?: number;
  tierOptions?: { id: string; name: string; price: string; responseTime: string }[];
  detailField?: string;
  detailType?: "textarea" | "select";
  detailPlaceholder?: string;
  detailOptions?: { id: string; label: string }[];
  detailRows?: number;
}

interface SectionIntro {
  id: string;
  type: "section-intro";
  title: string;
  subtitle: string;
}

type QuestionItem = QuestionMeta | SectionIntro;

function isSectionIntro(q: QuestionItem): q is SectionIntro {
  return (q as SectionIntro).type === "section-intro";
}

const COMMON_QUESTIONS: QuestionMeta[] = [
  {
    id: "problemToSolve", section: "common", field: "problemToSolve",
    title: "What problem are you trying to solve?",
    subtitle: "Describe the core problem or opportunity you want to address.",
    type: "textarea", placeholder: "Tell us about the challenge you're facing...", rows: 4, required: true,
  },
  {
    id: "successCriteria", section: "common", field: "successCriteria",
    title: "What does success look like?",
    subtitle: "How will you measure whether this project achieved its goals?",
    type: "textarea", placeholder: "Describe your success criteria...", rows: 3, required: true,
  },
  {
    id: "timeline", section: "common", field: "timeline",
    title: "What's your timeline expectation?",
    subtitle: "When do you need this delivered?",
    type: "select", options: TIMELINE_OPTIONS, required: true,
  },
  {
    id: "technicalEnvironment", section: "common", field: "technicalEnvironment",
    title: "What's your current technical environment?",
    subtitle: "Existing code, desktop framework, or tools you're using.",
    type: "textarea", placeholder: "e.g. Electron prototype, Figma designs, no code yet...", rows: 3,
  },
  {
    id: "constraints", section: "common", field: "constraints",
    title: "Any constraints or hard dependencies?",
    subtitle: "Deadlines, regulatory requirements, budget limits, third-party dependencies...",
    type: "textarea", placeholder: "List any constraints...", rows: 3,
  },
  {
    id: "decisionMakers", section: "common", field: "decisionMakers",
    title: "Who are the decision-makers?",
    subtitle: "Names and roles of people involved in the decision.",
    type: "input", placeholder: "e.g. John Smith (CTO), Jane Doe (VP Engineering)", required: true,
  },
];

const slideVariants = {
  enter: (direction: number) => ({
    y: direction === 0 ? 10 : direction > 0 ? 24 : -24,
    opacity: 0,
  }),
  center: { y: 0, opacity: 1 },
  exit: (direction: number) => ({
    y: direction > 0 ? -24 : 24,
    opacity: 0,
  }),
};

export default function AsyncQuestionnairePage() {
  const navigate = useNavigate();
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);
  const [direction, setDirection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [currentIndex, setCurrentIndex] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved).currentIndex ?? 0;
    } catch {}
    return 0;
  });

  const [common, setCommon] = useState<CommonQuestionnaireData>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved).common ?? initialCommon;
    } catch {}
    return initialCommon;
  });
  const questions: QuestionItem[] = useMemo(() => COMMON_QUESTIONS, []);

  useEffect(() => {
    const ctx = getLeadContext();
    if (!ctx || !ctx.emailVerified) {
      navigate("/verify/email");
      return;
    }
    if (ctx.ndaRequired && !ctx.ndaSigned && ctx.mode !== "call") {
      navigate("/verify/nda");
      return;
    }
    setLeadContext(ctx);

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.common) setCommon(parsed.common);
        if (parsed.currentIndex !== undefined) setCurrentIndex(parsed.currentIndex);
      }
    } catch {}
  }, [navigate]);

  const saveProgress = useCallback(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ common, currentIndex }),
      );
    } catch {}
  }, [common, currentIndex]);

  useEffect(() => {
    saveProgress();
  }, [saveProgress]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  useEffect(() => {
    if (currentIndex >= questions.length) {
      setCurrentIndex(Math.max(0, questions.length - 1));
    }
  }, [questions.length, currentIndex]);

  const currentQuestion = questions[currentIndex] ?? null;
  const isOnLastQuestion = currentIndex === questions.length - 1;

  const getValue = useCallback((q: QuestionMeta): unknown => {
    return common[q.field as keyof CommonQuestionnaireData];
  }, [common]);

  const setValue = useCallback((q: QuestionMeta, value: unknown) => {
    setCommon((prev) => ({ ...prev, [q.field]: value }));
  }, []);

  const getDetailValue = useCallback((q: QuestionMeta): unknown => {
    if (!q.detailField) return "";
    return common[q.detailField as keyof CommonQuestionnaireData];
  }, [common]);

  const setDetailValue = useCallback((q: QuestionMeta, value: unknown) => {
    if (!q.detailField) return;
    const field = q.detailField as keyof CommonQuestionnaireData;
    setCommon((prev) => ({ ...prev, [field]: value }));
  }, []);

  const currentAnswered = (() => {
    if (!currentQuestion) return false;
    if (isSectionIntro(currentQuestion)) return true;
    const q = currentQuestion;
    if (!q.required) return true;
    const value = getValue(q);
    switch (q.type) {
      case "textarea":
      case "input":
        return typeof value === "string" && value.trim() !== "";
      case "select":
      case "tier-select":
        return typeof value === "string" && value !== "";
      case "multiselect":
        return Array.isArray(value) && value.length > 0;
      case "yes-no":
        return true;
      default:
        return true;
    }
  })();

  const goNext = useCallback(() => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    if (currentIndex >= questions.length - 1) return;
    setDirection(1);
    setCurrentIndex((i: number) => i + 1);
  }, [currentIndex, questions.length]);

  const goBack = useCallback(() => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex((i: number) => i - 1);
    }
  }, [currentIndex]);

  const handleAutoAdvance = useCallback(() => {
    if (currentIndex >= questions.length - 1) return;
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    autoAdvanceTimer.current = setTimeout(() => {
      setDirection(1);
      setCurrentIndex((i: number) => i + 1);
    }, 400);
  }, [currentIndex, questions.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (isOnLastQuestion) return;
      if (e.key === "Enter" && currentAnswered) {
        e.preventDefault();
        goNext();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentAnswered, goNext, isOnLastQuestion]);

  const handleSubmit = async () => {
    if (!leadContext) return;
    setIsSubmitting(true);

    const submission: QuestionnaireSubmission = {
      common,
    };

    localStorage.setItem("echopeak-questionnaire-data", JSON.stringify(submission));
    localStorage.setItem(
      "echopeak-lead-context",
      JSON.stringify({ ...leadContext, questionnaireSubmittedAt: new Date().toISOString() }),
    );
    localStorage.removeItem(STORAGE_KEY);

    await new Promise((r) => setTimeout(r, 800));
    setIsSubmitting(false);
    navigate("/questionnaire/submitted");
  };

  if (!leadContext) return null;

  const totalQuestions = questions.length;
  const progress = ((currentIndex + 1) / totalQuestions) * 100;

  const keyboardHint = (() => {
    if (!currentQuestion || isSectionIntro(currentQuestion)) return "Press Enter ↵";
    switch (currentQuestion.type) {
      case "textarea": return "Ctrl + Enter ↵";
      case "input": return "Enter ↵";
      case "select":
      case "tier-select": return "Click to select";
      case "multiselect": return "Select options, then continue";
      default: return "";
    }
  })();

  return (
    <div className="min-h-screen flex flex-col">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <div className="relative z-10 px-6 pt-6">
        <div className="container mx-auto max-w-2xl">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/engage")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <span className="text-sm text-muted-foreground font-medium tabular-nums">
              {currentIndex + 1} / {totalQuestions}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (leadContext) {
                  localStorage.setItem(
                    "echopeak-lead-context",
                    JSON.stringify({ ...leadContext, engagementPath: "call" }),
                  );
                }
                navigate("/schedule");
              }}
            >
              <Calendar className="h-4 w-4 mr-2" />
              Switch to call
            </Button>
          </div>
          <div className="w-full bg-muted/50 rounded-full h-1">
            <motion.div
              className="bg-primary h-1 rounded-full"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center relative z-10 px-6 py-8">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait" custom={direction}>
            {currentQuestion && (
              <motion.div
                key={currentQuestion.id}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {isSectionIntro(currentQuestion) ? (
                  <div className="text-center py-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
                      <CheckCircle2 className="h-8 w-8 text-primary" />
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                      {currentQuestion.title}
                    </h2>
                    <p className="text-muted-foreground text-lg">
                      {currentQuestion.subtitle}
                    </p>
                  </div>
                ) : (
                  <QuestionRenderer
                    question={currentQuestion}
                    value={getValue(currentQuestion)}
                    onChange={(v) => setValue(currentQuestion, v)}
                    detailValue={getDetailValue(currentQuestion)}
                    onDetailChange={(v) => setDetailValue(currentQuestion, v)}
                    onAutoAdvance={handleAutoAdvance}
                    onNext={goNext}
                    isAnswered={currentAnswered}
                    isLastQuestion={isOnLastQuestion}
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="relative z-10 px-6 pb-8">
        <div className="container mx-auto max-w-2xl flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={currentIndex === 0}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>

          <div className="flex items-center gap-3">
            {isOnLastQuestion ? (
              <Button
                onClick={handleSubmit}
                disabled={!currentAnswered || isSubmitting}
                size="lg"
                className="gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={goNext}
                disabled={currentQuestion && !isSectionIntro(currentQuestion) && currentQuestion.required ? !currentAnswered : false}
                size="lg"
                className="gap-2"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {keyboardHint && (
          <div className="container mx-auto max-w-2xl mt-3 text-center">
            <span className="text-xs text-muted-foreground/50">{keyboardHint}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionRenderer({
  question,
  value,
  onChange,
  detailValue,
  onDetailChange,
  onAutoAdvance,
  onNext,
  isAnswered,
  isLastQuestion,
}: {
  question: QuestionMeta;
  value: unknown;
  onChange: (v: unknown) => void;
  detailValue: unknown;
  onDetailChange: (v: unknown) => void;
  onAutoAdvance: () => void;
  onNext: () => void;
  isAnswered: boolean;
  isLastQuestion: boolean;
}) {
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isAnswered && !isLastQuestion) {
      e.preventDefault();
      onNext();
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && isAnswered && !isLastQuestion) {
      e.preventDefault();
      onNext();
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-2">
          {question.title}
          {question.required && <span className="text-primary/70 ml-1">*</span>}
        </h2>
        {question.subtitle && (
          <p className="text-muted-foreground text-base md:text-lg">{question.subtitle}</p>
        )}
        {!question.required && (
          <span className="text-xs text-muted-foreground/70 bg-muted px-2 py-0.5 rounded-full mt-2 inline-block">
            optional
          </span>
        )}
      </div>

      <div>
        {question.type === "textarea" && (
          <Textarea
            autoFocus
            placeholder={question.placeholder}
            rows={question.rows ?? 4}
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            className="text-base md:text-lg resize-none"
          />
        )}

        {question.type === "input" && (
          <Input
            autoFocus
            placeholder={question.placeholder}
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleInputKeyDown}
            className="text-base md:text-lg h-12"
          />
        )}

        {question.type === "select" && question.options && (
          <div className="grid gap-2">
            {question.options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  onChange(opt.id);
                  if (!isLastQuestion) onAutoAdvance();
                }}
                className={cn(
                  "p-4 rounded-xl border-2 text-left transition-all duration-150",
                  "hover:bg-muted/50",
                  value === opt.id
                    ? "border-primary bg-primary/10 shadow-sm"
                    : "border-border hover:border-primary/40",
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
                    value === opt.id ? "border-primary bg-primary" : "border-muted-foreground/30",
                  )}>
                    {value === opt.id && <div className="w-2 h-2 rounded-full bg-primary-foreground" />}
                  </div>
                  <span className="text-base font-medium">{opt.label}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {question.type === "multiselect" && question.options && (
          <div className="flex flex-wrap gap-3">
            {question.options.map((opt) => {
              const selected = Array.isArray(value) && value.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    const current = (value as string[]) || [];
                    onChange(
                      selected
                        ? current.filter((v) => v !== opt.id)
                        : [...current, opt.id],
                    );
                  }}
                  className={cn(
                    "px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all duration-150",
                    selected
                      ? "border-primary bg-primary/10 text-primary shadow-sm"
                      : "border-border hover:border-primary/40 hover:bg-muted/50",
                  )}
                >
                  {selected && <CheckCircle2 className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {question.type === "tier-select" && question.tierOptions && (
          <div className="grid gap-3">
            {question.tierOptions.map((tier) => (
              <button
                key={tier.id}
                type="button"
                onClick={() => {
                  onChange(tier.id);
                  if (!isLastQuestion) onAutoAdvance();
                }}
                className={cn(
                  "p-5 rounded-xl border-2 text-left transition-all duration-150",
                  "hover:bg-muted/50",
                  value === tier.id
                    ? "border-primary bg-primary/10 shadow-sm"
                    : "border-border hover:border-primary/40",
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-lg">{tier.name}</span>
                  <span className="text-sm text-muted-foreground font-medium">{tier.price}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Response time: {tier.responseTime}
                </p>
              </button>
            ))}
          </div>
        )}

        {question.type === "yes-no" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => onChange(true)}
                className={cn(
                  "p-4 rounded-xl border-2 text-center font-medium text-base transition-all duration-150",
                  value === true
                    ? "border-primary bg-primary/10 text-primary shadow-sm"
                    : "border-border hover:border-primary/40 hover:bg-muted/50",
                )}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange(false);
                  if (question.detailField) onDetailChange("");
                }}
                className={cn(
                  "p-4 rounded-xl border-2 text-center font-medium text-base transition-all duration-150",
                  value === false && (value as boolean) !== undefined
                    ? "border-primary bg-primary/10 text-primary shadow-sm"
                    : "border-border hover:border-primary/40 hover:bg-muted/50",
                )}
              >
                No
              </button>
            </div>

            <AnimatePresence>
              {value === true && question.detailField && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {question.detailType === "select" && question.detailOptions ? (
                    <div className="grid gap-2 pt-2">
                      {question.detailOptions.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => onDetailChange(opt.id)}
                          className={cn(
                            "p-3 rounded-xl border-2 text-left transition-all duration-150",
                            detailValue === opt.id
                              ? "border-primary bg-primary/10 shadow-sm"
                              : "border-border hover:border-primary/40 hover:bg-muted/50",
                          )}
                        >
                          <span className="text-sm font-medium">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <Textarea
                      autoFocus
                      placeholder={question.detailPlaceholder}
                      rows={question.detailRows ?? 2}
                      value={(detailValue as string) || ""}
                      onChange={(e) => onDetailChange(e.target.value)}
                      onKeyDown={handleTextareaKeyDown}
                      className="text-base mt-2"
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
